import React, { useCallback, useEffect, useState } from 'react';

import { DebugContext } from 'App';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import {
	NavigationWrapper,
	SessionWrapper,
	TargetsAllianzUtilsWrapper,
	UiWrapper,
} from '@coinscrap/webapp-core';
import { MainModal } from 'components/MainModal/MainModal';
import { useGetError } from 'hooks/useGetError';

export const useTarification = (
	simulationStep,
	targetId,
	capital,
	otherCapital,
	resized,
	trigger,
	setTrigger,
	customOnError = undefined,
	agent,
) => {
	const { openModal } = UiWrapper.use();
	const { navigateTo } = NavigationWrapper.use();
	const { clearSession } = SessionWrapper.use();
	const isDebug = React.useContext(DebugContext);
	const { tarificate } = TargetsAllianzUtilsWrapper.use();
	const getError = useGetError();

	const onError = useCallback(
		(e) => {
			openModal(() => <MainModal type="error" text={getError(e)} />, {
				disableClickAway: true,
				closeButton: true,
				modalClass: isMobile ? 'modal-xl' : 'modal-main',
				key: 'modal-useTarification-KO',
			});
		},
		[getError, openModal],
	);

	const [startProcess, setStartProcess] = useState(false);

	const [packages, setPackages] = useState([]);
	const [resumePdf, setResumePdf] = useState('');
	const [policyDates, setPolicyDates] = useState({});
	const [loadingPacks, setLoadingPacks] = useState('Obteniendo tarifas');

	useEffect(() => {
		if (simulationStep === 2) {
			setStartProcess(true);
		} else {
			setStartProcess(false);
			setPackages([]);
			setLoadingPacks('Obteniendo tarifas');
		}
		return () => {};
	}, [simulationStep]);

	const getTarification = useCallback(
		async (capital) => {
			if (!capital) return;

			try {
				setLoadingPacks('Obteniendo tarifas');

				let tarification;
				try {
					tarification = await tarificate(targetId, capital);
				} catch (e) {
					console.log('❌ ERROR', e);
					setPackages([]);
					setResumePdf('');
					if (customOnError) {
						customOnError(
							e,
							setTrigger,
							openModal,
							navigateTo,
							isDebug,
							clearSession,
							agent,
						);
					} else {
						onError(e);
					}
					return;
				}

				console.log('✅ tarificate', tarification);

				if (tarification?.operationData?.allianzOperationId) {
					// PACKAGES
					let packages = tarification?.operationData?.priceGrid?.modality.modalityId
						? [tarification?.operationData?.priceGrid?.modality]
						: tarification?.operationData?.priceGrid?.modality;

					// Filtrado por periodicidad Anual
					packages = packages?.filter((pack) => pack.paymentType === 'A');

					// Filtrado por Modalidad
					packages = packages?.filter(
						(pack) =>
							pack.modalityDescription === 'BÁSICO' ||
							pack.modalityDescription === 'ÓPTIMO' ||
							pack.modalityDescription === 'EXTRA',
					);

					// No debería haber más de 3 packs
					if (packages.length > 3) {
						packages = packages.slice(0, 3);
					} else if (packages.length === 0) {
						setPackages([]);
						setResumePdf('');
						setPolicyDates({});
						customOnError(
							{ safeMessage: 'No tiene modalidades contratables' },
							setTrigger,
							openModal,
							navigateTo,
							isDebug,
							clearSession,
						);
						return;
					}

					packages = packages.map((pack) => {
						return {
							...pack,
							totalPremiumAmountFloat: parseFloat(pack.totalPremiumAmount),
						};
					});
					setPackages(packages);
					setResumePdf(tarification?.resumePdf);
					setPolicyDates({
						policyEffectiveDate: moment(
							tarification?.operationData?.policyDates.policyEffectiveDate,
						).format('DD/MM/YYYY'),
						policyExpirationDate: moment(
							tarification?.operationData?.policyDates.policyExpirationDate,
						).format('DD/MM/YYYY'),
					});
				} else {
					setPackages([]);
					setResumePdf('');
					setPolicyDates({});
				}
			} catch (e) {
				if (customOnError) {
					customOnError(e, setTrigger, openModal, navigateTo, isDebug, clearSession);
				} else {
					onError(e);
				}
			} finally {
				setLoadingPacks(undefined);
			}
		},
		[
			tarificate,
			targetId,
			customOnError,
			setTrigger,
			openModal,
			navigateTo,
			isDebug,
			clearSession,
			agent,
			onError,
		],
	);

	useEffect(() => {
		const timeOutId = setTimeout(() => {
			if (!resized.current && startProcess) {
				let price = capital;
				if (capital === 'Otros') {
					price = otherCapital;
				}
				getTarification(price);
			}
			if (resized.current) {
				const intTimeOutId = setTimeout(() => {
					resized.current = false;
				}, 2000);
				return () => clearTimeout(intTimeOutId);
			}
		}, 750);
		return () => clearTimeout(timeOutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [capital, otherCapital, resized, startProcess, trigger]);

	return { loadingPacks, packages, resumePdf, policyDates };
};
